
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Category, IncomingCallAnswered, IncomingCallStarted } from "@/api";
import {
  CustomerDetail,
  fetchCustomerDetail,
  getCachedCustomerDetail,
} from "@/utils/customerDetailCache";
import {
  elapsedSecondsBetween,
  formatDuration,
} from "@/utils/callDurationFormatter";
import { formatPhoneNumber } from "@/utils";

@Component({
  name: "CallQueueCard",
})
export default class CallQueueCard extends Vue {
  @Prop({ required: true }) readonly call!:
    | IncomingCallStarted
    | IncomingCallAnswered;

  @Prop({ type: Boolean, default: false }) readonly dark!: boolean;

  @Prop({ type: Boolean, default: false }) readonly active!: boolean;

  private iconResizeObserver: ResizeObserver | null = null;

  customerDetail: CustomerDetail | null = null;

  private now = Date.now();

  private tickIntervalId: number | null = null;

  mounted() {
    this.syncIconSquare();
    this.iconResizeObserver = new ResizeObserver(() => this.syncIconSquare());
    this.iconResizeObserver.observe(this.$el);
    this.loadCustomerDetail();
    this.tickIntervalId = window.setInterval(() => {
      this.now = Date.now();
    }, 1000);
  }

  @Watch("customerDetailKey")
  onCustomerDetailKeyChange() {
    this.loadCustomerDetail();
  }

  private loadCustomerDetail() {
    const customerId = this.customerId;
    if (customerId == null || this.category === "Driver") {
      this.customerDetail = null;
      return;
    }

    const cached = getCachedCustomerDetail(customerId);
    if (cached) {
      this.customerDetail = cached;
      return;
    }

    this.customerDetail = null;
    fetchCustomerDetail(customerId).then((detail) => {
      if (this.customerId === customerId) {
        this.customerDetail = detail;
      }
    });
  }

  private get customerId(): number | null {
    return this.call.customerId ?? null;
  }

  private get category(): Category | null {
    return "category" in this.call ? this.call.category ?? null : null;
  }

  private get customerDetailKey(): string {
    return `${this.customerId}|${this.category}`;
  }

  beforeDestroy() {
    this.iconResizeObserver?.disconnect();
    this.iconResizeObserver = null;
    if (this.tickIntervalId !== null) {
      window.clearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }
  }

  private syncIconSquare() {
    const el = this.$refs.icon as HTMLElement | undefined;
    if (el) {
      el.style.width = `${el.offsetHeight}px`;
    }
  }

  get displayPhone(): string {
    const phone = "phone" in this.call ? this.call.phone : null;
    const raw = phone || this.call.clid;
    return raw ? formatPhoneNumber(raw) : "neznámé číslo";
  }

  get answeredAt(): string | null {
    return "answeredAt" in this.call ? this.call.answeredAt ?? null : null;
  }

  get customerName(): string {
    const detail = this.customerDetail;
    if (detail) {
      const name = `${detail.firstName} ${detail.lastName}`.trim();
      const withAge = detail.age != null ? `${name} (${detail.age})` : name;
      if (withAge) {
        return withAge;
      }
    }
    return this.call.contactTitle || this.displayPhone;
  }

  get subContactName(): string | null {
    if (this.category === "Driver") {
      return null;
    }
    const isFromSubContact =
      "isFromSubContact" in this.call && this.call.isFromSubContact;
    if (!isFromSubContact) {
      return null;
    }
    return this.call.contactTitle || null;
  }

  get duration(): string {
    const reference = this.active
      ? this.answeredAt
      : "startedAt" in this.call
      ? this.call.startedAt ?? null
      : null;
    return formatDuration(elapsedSecondsBetween(reference, this.now));
  }

  get hasCustomer(): boolean {
    return "customerId" in this.call && this.call.customerId != null;
  }

  get detailTooltip(): string {
    return this.hasCustomer
      ? "Detail zákazníka"
      : "Volající není evidovaný zákazník";
  }

  get canTake(): boolean {
    return "activityName" in this.call && !!this.call.activityName;
  }

  get canRedirect(): boolean {
    return "activityName" in this.call && !!this.call.activityName;
  }

  get canHangup(): boolean {
    return "activityName" in this.call && !!this.call.activityName;
  }
}
